import {
  type DataSourceDefinition,
  type FiltersDefinition,
  type SortingsDefinition,
} from '@factorialco/f0-react'
import { HeadcountEmployeeSelectorDocument } from 'generated/resources/graphql'
import { type RecordType } from '@factorialco/f0-react'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import buildUrl from 'modules/core/lib/buildUrl'

export interface EmployeeOption extends RecordType {
  id: string
  fullName: string
  avatarUrl: string | undefined
}

interface UseHeadcountEmployeeSourceOptions {
  selfAndReportees?: boolean
}

const useHeadcountEmployeeSource = (
  options?: UseHeadcountEmployeeSourceOptions
): DataSourceDefinition<EmployeeOption, FiltersDefinition, SortingsDefinition> => {
  return useGraphqlDataSource(
    {
      document: HeadcountEmployeeSelectorDocument,
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-first',
      perPage: 25,
      search: { enabled: true },
      mapData: (data) =>
        data.employees.employeeNamesConnection.edges.map(
          (edge): EmployeeOption => ({
            id: edge.node.id,
            fullName: edge.node.fullName,
            avatarUrl: buildUrl(edge.node.avatar?.url),
          })
        ),
      mapPagination: (data) => {
        const connection = data.employees.employeeNamesConnection
        return {
          total: connection.totalCount ?? 0,
          cursor: connection.pageInfo.endCursor ?? '0',
          hasNextPage: !!connection.pageInfo.hasNextPage,
        }
      },
      mapVariables: ({ search, pagination }) => ({
        search,
        first: pagination?.perPage ?? 25,
        after:
          pagination?.cursor && pagination.cursor !== '0' ? String(pagination.cursor) : undefined,
        selfAndReportees: options?.selfAndReportees,
      }),
    },
    [options?.selfAndReportees]
  )
}

export default useHeadcountEmployeeSource
